import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, apiUpload } from '@/lib/http';

/** Faixa de espera pelos prazos da empresa (padrão do dono: 1 h atenção, 4 h crítico). */
export type WaitBand = 'OK' | 'ATTENTION' | 'CRITICAL';

export interface SupportSettings {
  attentionMinutes: number;
  criticalMinutes: number;
  /** Até quantos dias o que está sem resposta fica na fila; depois, some da tela. */
  replyWindowDays: number;
}

/** Contagens dentro do prazo, por faixa. */
type BandCounts = Record<WaitBand | 'total', number>;

export interface SupportPlatform {
  provider: string;
  displayName: string;
  questions: boolean;
  messages: boolean;
  limits: { answer: number; message: number } | null;
  stores: {
    id: string;
    name: string;
    checkedAt: string | null;
    permissionMissing: string | null;
  }[];
  counts: { questions: BandCounts; conversations: BandCounts };
}

export interface SupportOverview {
  settings: SupportSettings;
  channelWriteMode: 'disabled' | 'sandbox' | 'production';
  platforms: SupportPlatform[];
}

export interface SupportListing {
  id: string;
  title: string;
  price: string;
  availableStock: number;
  coverUrl: string | null;
  permalink: string | null;
  productId: string | null;
  sku: string | null;
}

interface Waiting {
  waitMinutes: number | null;
  waitBand: WaitBand | null;
}

export interface QuestionRow extends Waiting {
  id: string;
  externalId: string;
  store: string;
  text: string;
  rawStatus: string;
  open: boolean;
  askedAt: string;
  answerText: string | null;
  answeredAt: string | null;
  answeredInTroig: boolean;
  hasDraft: boolean;
  listing: SupportListing | null;
}

export interface Draft {
  text: string;
  byAi: boolean;
  at: string | null;
}

export interface QuestionDetail extends Omit<QuestionRow, 'hasDraft'> {
  provider: string;
  draft: Draft | null;
  maxLength: number | null;
  previousAnswers: { question: string; answer: string }[];
}

export interface ConversationOrder {
  id: string;
  externalId: string;
  orderedAt: string;
  stage: string;
  stageLabel: string;
  deliveredAt: string | null;
  items: { title: string; quantity: number }[];
}

export interface ConversationRow extends Waiting {
  id: string;
  externalId: string;
  store: string;
  blocked: boolean;
  awaitingReply: boolean;
  lastMessage: { text: string; fromSeller: boolean; at: string } | null;
  lastMessageAt: string | null;
  hasDraft: boolean;
  order: ConversationOrder | null;
}

export interface ConversationDetail extends Waiting {
  id: string;
  externalId: string;
  store: string;
  provider: string;
  blocked: boolean;
  awaitingReply: boolean;
  draft: Draft | null;
  maxLength: number | null;
  order: ConversationOrder | null;
  messages: {
    id: string;
    fromSeller: boolean;
    text: string;
    sentAt: string;
    sentInTroig: boolean;
    attachments: MessageAttachment[];
  }[];
}

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface SupportFilter {
  provider: string;
  connectionId?: string;
  /** `CLAIM` e `RETURN` só nas conversas pós-venda (venda com reclamação / com devolução). */
  state: 'OPEN' | 'ANSWERED' | 'CLAIM' | 'RETURN';
  /** Número do pedido ou do carrinho (só conversas). */
  search?: string;
  page: number;
}

/** Anexo de mensagem: o arquivo fica na plataforma; o troig busca na hora (ADR-076). */
export interface MessageAttachment {
  id: string;
  name: string;
  type: string | null;
}

export interface Suggestion {
  text: string;
  canAnswer: boolean;
  missingInfo: string[];
  model: string;
}

export const supportKeys = {
  all: ['support'] as const,
  overview: () => [...supportKeys.all, 'overview'] as const,
  questions: (filter: SupportFilter) => [...supportKeys.all, 'questions', filter] as const,
  question: (id: string) => [...supportKeys.all, 'question', id] as const,
  conversations: (filter: SupportFilter) => [...supportKeys.all, 'conversations', filter] as const,
  conversation: (id: string) => [...supportKeys.all, 'conversation', id] as const,
};

function query(filter: SupportFilter): string {
  return new URLSearchParams({
    provider: filter.provider,
    state: filter.state,
    page: String(filter.page),
    ...(filter.connectionId ? { connectionId: filter.connectionId } : {}),
    ...(filter.search ? { busca: filter.search } : {}),
  }).toString();
}

/** A fila muda sozinha (avisos da plataforma): relê a cada minuto. */
const LIVE = 60_000;

export function useSupportOverview() {
  return useQuery({
    queryKey: supportKeys.overview(),
    queryFn: () => apiRequest<SupportOverview>('/support/overview'),
    refetchInterval: LIVE,
  });
}

export function useQuestions(filter: SupportFilter) {
  return useQuery({
    queryKey: supportKeys.questions(filter),
    queryFn: () => apiRequest<Page<QuestionRow>>(`/support/questions?${query(filter)}`),
    placeholderData: keepPreviousData,
    refetchInterval: LIVE,
  });
}

export function useQuestion(id: string) {
  return useQuery({
    queryKey: supportKeys.question(id),
    queryFn: () => apiRequest<QuestionDetail>(`/support/questions/${id}`),
  });
}

export function useConversations(filter: SupportFilter) {
  return useQuery({
    queryKey: supportKeys.conversations(filter),
    queryFn: () => apiRequest<Page<ConversationRow>>(`/support/conversations?${query(filter)}`),
    placeholderData: keepPreviousData,
    refetchInterval: LIVE,
  });
}

export function useConversation(id: string) {
  return useQuery({
    queryKey: supportKeys.conversation(id),
    queryFn: () => apiRequest<ConversationDetail>(`/support/conversations/${id}`),
  });
}

type Kind = 'questions' | 'conversations';

/** Rascunho, sugestão e envio — as três ações do atendimento, para pergunta e conversa. */
export function useSupportActions(kind: Kind, id: string) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: supportKeys.all });
  const base = `/support/${kind}/${id}`;
  return {
    saveDraft: useMutation({
      mutationFn: (text: string) =>
        apiRequest<void>(`${base}/draft`, { method: 'PUT', body: { text } }),
    }),
    suggest: useMutation({
      mutationFn: () => apiRequest<Suggestion>(`${base}/suggest`, { method: 'POST' }),
    }),
    send: useMutation({
      mutationFn: (input: { text: string; attachments?: string[] }) =>
        apiRequest<void>(`${base}/${kind === 'questions' ? 'answer' : 'reply'}`, {
          method: 'POST',
          body: kind === 'questions' ? { text: input.text } : input,
        }),
      onSuccess: refresh,
    }),
  };
}

/** Sobe a foto ou o PDF direto para a plataforma; o id volta para ir junto da resposta. */
export function useUploadAttachment(conversationId: string) {
  return useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append('file', file);
      return apiUpload<{ id: string; name: string }>(
        `/support/conversations/${conversationId}/attachments`,
        body,
      );
    },
  });
}

/** O arquivo do anexo (com a sessão de quem pede): vira endereço local para mostrar ou baixar. */
export function useAttachmentFile(conversationId: string, attachmentId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...supportKeys.conversation(conversationId), 'attachment', attachmentId],
    queryFn: () =>
      apiRequest<Blob>(`/support/conversations/${conversationId}/attachments/${attachmentId}`, {
        parse: 'blob',
      }),
    enabled,
    staleTime: Infinity,
  });
}

export function useUpdateSupportSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settings: SupportSettings) =>
      apiRequest<void>('/support/settings', { method: 'PUT', body: settings }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: supportKeys.all }),
  });
}
